/**
 * StaleDataBanner — a non-blocking notice, shown ABOVE the hero, that the
 * collector has fallen behind. Live data still renders below it: the banner
 * never blocks the numbers, it just qualifies them honestly (DESIGN.md — "every
 * state designed … degraded paths that stay dignified").
 *
 * Driven off `/api/status` via `collectorLevel()` (lib/status): it shows only
 * for `delayed` / `down`; it is HIDDEN for `live` and for `unknown` (no poll
 * since a deploy — we don't cry wolf on a fresh restart, #89 Q1). Reuses the
 * existing bucketing, so there are no new thresholds.
 *
 * a11y: colour is never the only channel — the state word + the dot's shape
 * carry the meaning, and coloured label text uses the AA-contrast token twins
 * (`--status-delayed-text`), never the vivid dot hue. `role="note"` (NOT
 * `role="alert"` / `aria-live`) so #181 can layer live-region announcements
 * later without double-speaking (#89 Q2).
 *
 * Prop-driven presentation. The one side effect is scroll preservation (below):
 * because the banner is driven by a SEPARATE `/api/status` query, it can appear
 * a beat after the hero has painted; inserting it above scrolled content would
 * otherwise shove what the user is reading down its own height. #104 disables
 * native scroll-anchoring app-wide (`overflow-anchor: none` on `<html>`, so its
 * `restoreScroll` rAF loop is the sole scroll authority), which means the
 * browser will NOT absorb this insertion — so we offset by the banner's
 * footprint ourselves, and we are the *single* compensator (no double-count).
 *
 * That offset fires ONLY on a genuine in-place `live → stale` transition: the
 * query previously resolved to a healthy (live) reading — so content was already
 * laid out WITHOUT the banner — and it has now degraded while the user sits
 * mid-page. It deliberately does NOT fire on mount, route-change, or a
 * pending/unknown→stale first resolution (prev !== "live"): those coincide with
 * scroll RESTORATION (the `restoreScroll` rAF re-apply loop), which owns
 * scroll then — a compensating `scrollBy` would fight it and land the user ~80px
 * off. This gate is self-contained: it reads
 * only the previous vs current level, never the ambient scroll state or a warm
 * `/api/status` cache.
 */

import { useLayoutEffect, useRef } from "react";
import type { CollectorLevel } from "@/lib/status";
import { isMorphing } from "@/lib/morph";

interface StaleDataBannerProps {
  /** Collector health level (from `collectorLevel()`); only `delayed`/`down` render. */
  level: CollectorLevel;
  /** Age phrase for the last successful poll ("20 min fa"), or `null` when unknown. */
  ageLabel: string | null;
}

// ── i18n strings (all user-visible copy in one place) ─────────────────────────

const STRINGS = {
  delayed: {
    title: "Dati in ritardo",
    /** With the last-success age when known; the numbers below still stand. */
    body: (age: string | null) =>
      age
        ? `Aggiornato ${age}. I numeri qui sotto potrebbero non essere gli ultimi.`
        : "I numeri qui sotto potrebbero non essere gli ultimi.",
  },
  down: {
    title: "Aggiornamento interrotto",
    body: (age: string | null) =>
      age
        ? `Nessun nuovo dato da ${age}. Mostriamo l'ultima rilevazione disponibile.`
        : "Al momento non riceviamo nuovi dati. Mostriamo l'ultima rilevazione disponibile.",
  },
} as const;

export function StaleDataBanner({ level, ageLabel }: StaleDataBannerProps) {
  const shown = level === "delayed" || level === "down";

  // The level at the previous commit — the sole input to the compensation gate.
  // Initialised to the mount level so the first effect run sees `prev === level`
  // and can never treat mount as a transition.
  const prevLevelRef = useRef<CollectorLevel>(level);
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const prev = prevLevelRef.current;
    prevLevelRef.current = level;
    // Only a genuine in-place live→stale degradation compensates (see header):
    // prev must be a resolved healthy reading, now stale, and not mid-morph.
    // Everything else — mount, route-change, pending/unknown→stale — is left to
    // #104's scroll restoration so we never fight it.
    if (prev !== "live" || !shown || isMorphing()) return;
    const el = rootRef.current;
    if (!el || window.scrollY <= 0) return; // at the top: let the page grow down
    // The banner is the first item of a flex-column container, so inserting it
    // grows the page by its own height PLUS one container `gap`. Use the
    // fractional `getBoundingClientRect().height` (not the integer-rounded
    // `offsetHeight`) so the offset matches the real insertion to sub-pixel.
    const parent = el.parentElement;
    const gap = parent ? parseFloat(window.getComputedStyle(parent).rowGap || "0") : 0;
    const footprint = el.getBoundingClientRect().height + (Number.isFinite(gap) ? gap : 0);
    // `behavior: "instant"` overrides the app's global smooth scroll — the
    // compensation must SNAP in the same frame the banner mounts (before paint,
    // this being a layout effect), not glide, or the user sees a lurch.
    window.scrollBy({ top: footprint, left: 0, behavior: "instant" });
  }, [level, shown]);

  // `live` and `unknown` never warn (see file header).
  if (!shown) return null;

  const copy = STRINGS[level];

  return (
    <div className={`stale-banner stale-banner--${level}`} role="note" ref={rootRef}>
      <span className="stale-banner__dot" aria-hidden="true" />
      <p className="stale-banner__text">
        <span className="stale-banner__title">{copy.title}</span>{" "}
        <span className="stale-banner__body">{copy.body(ageLabel)}</span>
      </p>
    </div>
  );
}
